import React, { Component } from "react";
import SignUpForm from "../../components/SignUpForm";
import SeoHead from "../../components/SeoHead";
import TrustedBusinesses from "../../components/TrustedBusinesses";
import LandingHeroSlides from "../../components/LandingHeroSlides";
import { MERCHANT_PORTAL_BASE } from "../../constants/seo";
import {
  QIKTRUCK_APP_STORE,
  QIKTRUCK_CONSUMER_SITE,
  QIKTRUCK_PLAY_STORE,
} from "../../constants/mobileApp";
import {
  QIKTRUCK_PRIVACY_URL,
  QIKTRUCK_TERMS_URL,
} from "../../constants/legal";
import "./product-landing.css";

const FEATURES = [
  {
    icon: "mdi-truck-fast",
    title: "Book & track deliveries",
    text: "Manage once-off and recurring trips, live driver tracking, and order history in one place.",
  },
  {
    icon: "mdi-file-chart",
    title: "Analytics & billing",
    text: "Cost breakdowns, usage reports, and transparent billing for your business account.",
  },
  {
    icon: "mdi-layers-triple",
    title: "Scale with API",
    text: "Quote and book many trips from your ERP or backend via the Partner API.",
  },
  {
    icon: "mdi-puzzle",
    title: "Website widget",
    text: "Embed live delivery quotes on your store - shoppers checkout on QikTruck.",
  },
  {
    icon: "mdi-api",
    title: "Partner API",
    text: "Connect ERP, TMS, or custom backends with provisioned API keys.",
  },
  {
    icon: "mdi-account-group",
    title: "Team-ready",
    text: "Built for retailers, manufacturers, and ops teams who ship at scale.",
  },
];

class ProductLandingPage extends Component {
  constructor(props) {
    super(props);
    this.state = { menuOpen: false, authStep: "email" };
  }

  closeMenu = () => this.setState({ menuOpen: false });

  renderNavLinks(onClick) {
    const link = (href, label) => (
      <a key={href} href={href} onClick={onClick}>
        {label}
      </a>
    );
    return (
      <>
        {link("#features", "Features")}
        {link("/integrations?type=widget", "Integrations")}
        {link("/api-docs?product=embed", "Widget docs")}
        {link("/api-docs?product=partner", "Partner API")}
        {link("/login", "Log in")}
      </>
    );
  }

  render() {
    const { menuOpen, authStep } = this.state;
    const isLogin = this.props.variant === "login";
    const onOtpStep = authStep === "otp";

    return (
      <div className="qt-landing">
        <SeoHead
          title={
            isLogin
              ? "Sign in | QikTruck Business Portal"
              : "QikTruck Business Portal | Delivery dashboard for merchants"
          }
          description={
            isLogin
              ? "Sign in to the QikTruck Business Portal. Desktop-first dashboards for billing, integrations, and Partner API."
              : "Book and manage business deliveries in South Africa. Live driver tracking, billing, website widget embed, and Partner API for retailers and developers."
          }
          pathname={isLogin ? "/login" : "/"}
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "QikTruck Business Portal",
            url: MERCHANT_PORTAL_BASE,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            description:
              "Delivery dashboard for South African merchants with analytics, widget, and API integrations.",
            offers: { "@type": "Offer", price: "0", priceCurrency: "ZAR" },
          }}
        />
        <header className="qt-landing__header">
          <a className="qt-landing__logo" href="/">
            QIKTRUCK
          </a>
          <nav className="qt-landing__nav" aria-label="Main">
            {this.renderNavLinks()}
          </nav>
          <div className="qt-landing__header-actions">
            <a className="qt-landing__btn-ghost qt-landing__btn-ghost--nav" href="/login">
              Log in
            </a>
            <a className="qt-landing__btn-primary" href="/login">
              Business login
            </a>
            <button
              type="button"
              className="qt-landing__menu-toggle"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => this.setState({ menuOpen: true })}
            >
              <i className="mdi mdi-menu" />
            </button>
          </div>
        </header>

        {menuOpen && (
          <>
            <div
              className="qt-landing__drawer-backdrop"
              onClick={this.closeMenu}
              aria-hidden
            />
            <nav className="qt-landing__drawer" aria-label="Mobile menu">
              <button
                type="button"
                className="qt-landing__drawer-close"
                aria-label="Close menu"
                onClick={this.closeMenu}
              >
                ×
              </button>
              {this.renderNavLinks(this.closeMenu)}
              <a href={`${QIKTRUCK_CONSUMER_SITE}/quote`} onClick={this.closeMenu}>
                Get a transport quote →
              </a>
              <a href={QIKTRUCK_APP_STORE} target="_blank" rel="noopener noreferrer">
                Download iOS app
              </a>
              <a href={QIKTRUCK_PLAY_STORE} target="_blank" rel="noopener noreferrer">
                Download Android app
              </a>
            </nav>
          </>
        )}

        <section className="qt-landing__hero">
          <div className="qt-landing__hero-copy">
            <span className="qt-landing__eyebrow">
              <i className="mdi mdi-briefcase-outline" /> Business portal
            </span>
            <h1 className="qt-landing__title">
              {isLogin ? "Sign in to your business account" : "Run your deliveries like a logistics pro"}
            </h1>
            <p className="qt-landing__lead">
              {isLogin
                ? "Enter your work email for a one-time sign-in code. For dashboards, billing, and integrations, use a desktop or laptop."
                : "QikTruck Business is your command centre - book trucks, track orders, manage billing, and plug delivery into your website or systems. Built for teams, not one-off app quotes."}
            </p>

            <div className="qt-landing__desktop-hint">
              <i className="mdi mdi-monitor-dashboard" />
              <span>
                For the full dashboard experience - analytics and integrations -
                sign in on a desktop or laptop.
              </span>
            </div>

            <div className="qt-landing__mobile-paths">
              <a
                className="qt-landing__path-card"
                href={`${QIKTRUCK_CONSUMER_SITE}/quote`}
              >
                <span className="qt-landing__path-icon qt-landing__path-icon--quote">
                  <i className="mdi mdi-calculator-variant" />
                </span>
                <span className="qt-landing__path-text">
                  <strong>Need a transport quote?</strong>
                  <span>Instant pricing on qiktruck.co.za - no account needed</span>
                </span>
              </a>
              <a
                className="qt-landing__path-card"
                href={QIKTRUCK_CONSUMER_SITE}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="qt-landing__path-icon qt-landing__path-icon--app">
                  <i className="mdi mdi-cellphone" />
                </span>
                <span className="qt-landing__path-text">
                  <strong>On your phone?</strong>
                  <span>Get the QikTruck customer app for iOS & Android</span>
                </span>
              </a>
            </div>

            <div className="qt-landing__signup-box">
              <div className="qt-landing__signup-label">
                {onOtpStep
                  ? "Enter the code we sent"
                  : isLogin
                    ? "Sign in with your email"
                    : "Create your business account"}
              </div>
              <SignUpForm
                mode={isLogin ? "login" : "signup"}
                onStepChange={(step) => this.setState({ authStep: step })}
              />
            </div>

            <a
              className="qt-landing__demo-link"
              href="mailto:support@qiktruck.co.za?subject=QikTruck%20Business%20demo"
            >
              Book a demo with our team
              <i className="mdi mdi-arrow-right" />
            </a>
          </div>

          <div className="qt-landing__visual">
            <div className="qt-landing__visual-frame">
              <LandingHeroSlides />
            </div>
          </div>
        </section>

        <section className="qt-landing__trusted" aria-label="Businesses that book with QikTruck">
          <div className="qt-landing__trusted-inner">
            <TrustedBusinesses variant="light" />
          </div>
        </section>

        <section className="qt-landing__features" id="features">
          <div className="qt-landing__features-inner">
            <h2 className="qt-landing__section-title">
              Everything your ops team needs
            </h2>
            <p className="qt-landing__section-sub">
              From a single furniture store to national retail - manage delivery
              without spreadsheets and phone calls.
            </p>
            <div className="qt-landing__grid">
              {FEATURES.map((f) => (
                <article key={f.title} className="qt-landing__feature">
                  <i className={`mdi ${f.icon}`} />
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="qt-landing__cta">
          <div className="qt-landing__cta-card">
            <h2>Connect your store or systems</h2>
            <p>
              Add a delivery widget to your website, or integrate with our
              Partner API for automated quoting and booking.
            </p>
            <div className="qt-landing__cta-actions">
              <a className="qt-landing__btn-light" href="/integrations?type=widget">
                Website widget
              </a>
              <a
                className="qt-landing__btn-outline"
                href="/api-docs?product=partner"
              >
                Partner API docs
              </a>
            </div>
          </div>
          <div className="qt-landing__stats">
            <div className="qt-landing__stat">
              <strong>10K+</strong>
              <span>Deliveries</span>
            </div>
            <div className="qt-landing__stat">
              <strong>5K+</strong>
              <span>Drivers</span>
            </div>
            <div className="qt-landing__stat">
              <strong>9</strong>
              <span>Provinces</span>
            </div>
            <div className="qt-landing__stat">
              <strong>4.8★</strong>
              <span>Rating</span>
            </div>
          </div>
        </section>

        <footer className="qt-landing__footer">
          <div className="qt-landing__footer-links">
            <a href={QIKTRUCK_PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              Privacy
            </a>
            <a href={QIKTRUCK_TERMS_URL} target="_blank" rel="noopener noreferrer">
              Terms
            </a>
            <a href="/support">Support</a>
            <a href={QIKTRUCK_CONSUMER_SITE}>Consumer site</a>
            <a href={`${QIKTRUCK_CONSUMER_SITE}/quote`}>Get a quote</a>
          </div>
          <p>© {new Date().getFullYear()} QikTruck (Pty) Ltd · South Africa</p>
        </footer>
      </div>
    );
  }
}

export default ProductLandingPage;
